import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar, ArrowRight, Tag, FileText, Download, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { normalizeImageUrl } from '../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NEWS_HERO = "https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=1920&q=80";

const newsletters = [
  {
    id: 'nl-8',
    title: 'SSMCH Newsletter',
    quarter: 'Oct - Dec 2024',
    issue: 'Issue 8',
    url: 'https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/le4esjzj_8_SSMCH_Newletter8_Oct24ToDec24.pdf',
  },
  {
    id: 'nl-7',
    title: 'SSMCH Newsletter',
    quarter: 'Jul - Sep 2024',
    issue: 'Issue 7',
    url: 'https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/aq24u1xr_7_SSMCH_Newsletter7_July24ToSept24.pdf',
  },
  {
    id: 'nl-6',
    title: 'SSMCH Newsletter',
    quarter: 'Apr - Jun 2024',
    issue: 'Issue 6',
    url: 'https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/ljse2u7m_6_SSMCH_Newsletter6_April24ToJune24.pdf',
  },
  {
    id: 'nl-5',
    title: 'SSMCH Newsletter',
    quarter: 'Jan - Mar 2024',
    issue: 'Issue 5',
    url: 'https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/ps6a08c7_5_SSMCH_Newsletter5_Jan24ToMarch24.pdf',
  },
  {
    id: 'nl-4',
    title: 'SSMCH Newsletter',
    quarter: 'Oct - Dec 2023',
    issue: 'Issue 4',
    url: 'https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/xrc37vpr_4_SSMCH_Newsletter4_Oct23ToDec23.pdf',
  },
  {
    id: 'nl-3',
    title: 'SSMCH Newsletter',
    quarter: 'Jul - Sep 2023',
    issue: 'Issue 3',
    url: 'https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/v228pge8_3_SSMCH_Newsletter3_July23ToSept23.pdf',
  },
  {
    id: 'nl-2',
    title: 'SSMCH Newsletter',
    quarter: 'Apr - Jun 2023',
    issue: 'Issue 2',
    url: 'https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/95yr0ihk_2_SSMCH_Newsletter2_April23ToJune23.pdf',
  },
  {
    id: 'nl-1',
    title: 'SSMCH Newsletter',
    quarter: 'Jan - Mar 2023',
    issue: 'Issue 1',
    url: 'https://customer-assets.emergentagent.com/job_8a8e0a6f-0c7b-46a1-897e-ba32ed762c97/artifacts/nqc8bhfj_1_SSMCH_Newsletter1_Jan23ToMarch23.pdf',
  },
];

// Sample news data (will be replaced by API data when available)
const sampleNews = [
  {
    id: '1',
    title: 'MBBS Admissions 2025-26 Now Open',
    content: 'Applications are now being accepted for the upcoming academic session. Eligible candidates are encouraged to apply through the official counselling process.',
    category: 'announcement',
    created_at: new Date().toISOString(),
    is_featured: true,
    image_url: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=600&q=80',
  },
  {
    id: '2',
    title: 'Annual Medical Conference Scheduled',
    content: 'SSMCH will host its annual medical conference featuring distinguished speakers from across the country. Registration details will be announced soon.',
    category: 'event',
    created_at: new Date(Date.now() - 86400000).toISOString(),
    is_featured: false,
    image_url: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=80',
  },
  {
    id: '3',
    title: 'New Laboratory Equipment Installation',
    content: 'State-of-the-art laboratory equipment has been installed in the Biochemistry and Microbiology departments, enhancing practical training facilities.',
    category: 'notice',
    created_at: new Date(Date.now() - 172800000).toISOString(),
    is_featured: false,
    image_url: 'https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=600&q=80',
  },
  {
    id: '4',
    title: 'Student Orientation Program',
    content: 'Orientation program for newly admitted MBBS students will be conducted at the beginning of the academic session. Parents are welcome to attend.',
    category: 'announcement',
    created_at: new Date(Date.now() - 259200000).toISOString(),
    is_featured: false,
    image_url: 'https://images.unsplash.com/photo-1562774053-701939374585?w=600&q=80',
  },
];

const categoryColors = {
  announcement: 'bg-[#4ECDC4] text-white',
  event: 'bg-[#D4A84B] text-white',
  notice: 'bg-[#9ACD32] text-white',
};

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredNews = selectedCategory === 'all' 
    ? news 
    : news.filter(item => item.category === selectedCategory);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  return (
    <div className="page-transition" data-testid="news-page">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-32 hero-overlay"
        style={{
          backgroundImage: `url(${NEWS_HERO})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="news-hero"
      >
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              Stay Updated
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white font-bold leading-tight mb-6">
              News & Updates
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Stay informed about the latest announcements, events, and academic notices from SSMCH.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-6 bg-white border-b border-gray-100" data-testid="filter-section">
        <div className="container-custom">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-gray-600">Filter by:</span>
            {['all', 'announcement', 'event', 'notice'].map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                className={`rounded-full capitalize ${
                  selectedCategory === category 
                    ? 'bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white' 
                    : 'border-gray-200 text-gray-600 hover:border-[#4ECDC4] hover:text-[#4ECDC4]'
                }`}
                onClick={() => setSelectedCategory(category)}
                data-testid={`filter-${category}`}
              >
                {category === 'all' ? 'All Updates' : category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="section-padding bg-[#F8F9FA]" data-testid="news-grid-section">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="border-0 shadow-sm">
                  <Skeleton className="h-48 w-full" />
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-20 mb-3" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredNews.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No updates found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredNews.map((item, index) => (
                <Card 
                  key={item.id}
                  className="card-hover border-0 shadow-sm group overflow-hidden"
                  data-testid={`news-item-${index}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={normalizeImageUrl(item.image_url) || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&q=80'}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {item.is_featured && (
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-[#D4A84B] text-white">Featured</Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Badge className={`${categoryColors[item.category]} capitalize`}>
                        {item.category}
                      </Badge>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar size={12} />
                        {formatDate(item.created_at)}
                      </span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-[#1A202C] mb-2 line-clamp-2 group-hover:text-[#4ECDC4] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                      {item.content}
                    </p>
                    <Link to={`/news/${item.id}`}>
                      <Button
                        variant="ghost"
                        className="p-0 h-auto text-[#4ECDC4] hover:text-[#3DBDB4] font-medium text-sm"
                        data-testid={`read-more-${item.id}`}
                      >
                        Read More
                        <ArrowRight size={14} className="ml-1" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletters Section */}
      <section className="section-padding bg-white" data-testid="newsletters-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block bg-[#D4A84B]/10 text-[#D4A84B] px-4 py-2 rounded-full text-sm font-medium mb-4">
              College Publications
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A202C] mb-4">
              Quarterly Newsletters
            </h2>
            <p className="text-gray-600 text-lg">
              Stay connected with SSMCH through our quarterly newsletters featuring campus highlights, academic achievements, and institutional milestones.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {newsletters.map((nl, index) => (
              <Card
                key={nl.id}
                className="card-hover border-0 shadow-sm hover:shadow-xl group overflow-hidden rounded-xl"
                data-testid={`newsletter-${index}`}
              >
                <CardContent className="p-0">
                  <div className="relative bg-gradient-to-br from-[#D4A84B] via-[#C49A3F] to-[#B08A32] p-6 text-center overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
                    <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-6 -translate-x-6" />
                    <div className="relative z-10">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-3">
                        <FileText className="text-white" size={28} />
                      </div>
                      <p className="text-white/80 text-xs font-medium tracking-widest uppercase">Quarterly</p>
                      <p className="text-white font-heading font-bold text-lg mt-0.5">{nl.issue}</p>
                    </div>
                  </div>
                  <div className="p-5 text-center bg-white">
                    <p className="text-sm font-semibold text-[#1A202C] mb-1">{nl.quarter}</p>
                    <p className="text-xs text-gray-400 mb-4">SSMCH Newsletter</p>
                    <div className="flex gap-2 justify-center">
                      <a href={nl.url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="text-xs gap-1.5 bg-[#D4A84B] hover:bg-[#C49A3F] text-white shadow-sm">
                          <ExternalLink size={12} />View
                        </Button>
                      </a>
                      <a href={nl.url} download>
                        <Button size="sm" variant="outline" className="text-xs gap-1.5 border-[#D4A84B]/30 text-[#D4A84B] hover:bg-[#D4A84B]/5">
                          <Download size={12} />PDF
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1A202C]" data-testid="news-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Want to Join SSMCH?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Explore our admission process and become part of our medical education community.
          </p>
          <Link to="/admissions">
            <Button 
              className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-8 h-12 font-medium shadow-lg hover:shadow-xl transition-all"
              data-testid="news-apply-btn"
            >
              View Admission Details
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default News;
